import { Suspense, forwardRef, useEffect, useRef, useState } from "react";
import {
  /* Redirect, */
  Route,
} from "react-router-dom";

import styled from "styled-components/macro";

import FnrLoadingCarousel from "UI/FnrLoadingCarousel";

import { useGetUserData } from "./API/queries";
import { useIsLoggedIn } from "./stateHooks";

/**
 * @param   Component {import("react-router-dom").RouteProps["component"]}
 * @param   props     {Omit<import("react-router-dom").RouteProps, "component">}
 * @returns           {JSX.Element}
 */
export default function PrivateRoute({ component: Component, ...props }) {
  /**
   * We do not want to redirect before we have properly determined the login
   * status, so we use this (leaky) means to wait.
   * `useIsLoggedIn` uses `useGetUserData` under the hood... if this pattern
   * needs to be reused anywhere, it may be worth a refactor to not leak
   * our state hooks.
   * e.g. a refactor would allow us to do something along the lines of:
   *   `const { isLoginStatusPending, isLoggedIn } = useIsLoggedIn();`
   */
  const { isLoading } = useGetUserData();
  const isLoggedIn = useIsLoggedIn();

  // @todo a future iteration could include a 'hasIdledTooLong` functionality,
  //       likely with some other piece of "Delight & Surprise" UI
  //       e.g. "Hey, we logged you out since it's been a while since you took
  //             any actions, and because... security!"

  // @todo a future iteration could be clever enough to determine if the cause
  //       for a bounce was because Betty logged out in another tab,
  //       and could include some other "Delight & Surprise" UI
  //       e.g. "We noticed you may have logged out in another tab, and so we
  //            went ahead and brought you back to the login screen
  //            in this one!"

  useEffect(() => {
    if (!(isLoading || isLoggedIn)) {
      // use a hard navigation to ensure we get fresh (correct) Portal containers
      window.location.replace(
        `/login?redirect=${window.location.pathname}${window.location.search}`
      );
    }
  }, [isLoading, isLoggedIn]);

  // @todo a future iteration could make all changes necessary to just use a
  //       client-side navigation
  // const location = RRD.useLocation();
  // if (!(isPending || isLoggedIn)) {
  //   return <Redirect to={`/login?redirect=${location.pathname}${location.search}`} />
  // }

  return (
    <Route {...props}>
      <WithSuspendedLoading>
        <Component />
      </WithSuspendedLoading>
    </Route>
  );
}

/**
 * Gracefully renders a loading state while (lazy) children are loaded.
 * If lazy children are resolved quickly, we forego showing the loading state
 *   at all.
 * If lazy children are resolved _just after_ we start showing the
 *   loading state, we stall rendering them to prevent any flicker.
 * Otherwise, we just show the loading state until the lazy children are loaded.
 * @param   children {import("react").ReactNode}
 * @returns          {JSX.Element}
 */
function WithSuspendedLoading({ children }) {
  const [shouldShowLoader, setShouldShowLoader] = useState(false);
  const [isPreventingFlicker, setIsPreventingFlicker] = useState(false);
  const showLoaderTimeoutRef = useRef(-1);
  const preventFlickerTimeoutRef = useRef(-1);
  const [hasSetShowLoaderTimeout, setHasSetShowLoaderTimeout] = useState(false);

  useEffect(() => {
    showLoaderTimeoutRef.current = setTimeout(() => {
      setShouldShowLoader(true);
      setIsPreventingFlicker(true);
      preventFlickerTimeoutRef.current = setTimeout(() => {
        setIsPreventingFlicker(false);
      }, 500);
    }, 400);
    setHasSetShowLoaderTimeout(true);

    return () => {
      clearTimeout(showLoaderTimeoutRef.current);
      clearTimeout(preventFlickerTimeoutRef.current);
    };
  }, []);

  return (
    <Suspense
      fallback={
        <SDiv>
          <FnrLoadingCarousel show={shouldShowLoader} />
        </SDiv>
      }
    >
      <FlickerPreventer
        isPreventingFlicker={isPreventingFlicker}
        hasSetShowLoaderTimeout={hasSetShowLoaderTimeout}
        ref={showLoaderTimeoutRef}
      >
        {children}
      </FlickerPreventer>
    </Suspense>
  );
}

const SDiv = styled.div`
  position: relative;
  height: 100vh;
  width: calc(100vw - var(--nav-width));
  left: var(--nav-width);
`;

/**
 * @type {React.ForwardRefExoticComponent<React.PropsWithoutRef<{isPreventingFlicker: boolean, children: import("react").ReactNode}> & React.RefAttributes<number>>}
 */
const FlickerPreventer = forwardRef(MFlickerPreventer);

/**
 * @param props
 * @param props.isPreventingFlicker     {boolean}
 * @param props.hasSetShowLoaderTimeout {boolean}
 * @param props.children                {import("react").ReactNode}
 * @param showLoaderTimeoutRef          {import("react").MutableRefObject<number>}
 * @returns                             {import("react").ReactNode}
 */
function MFlickerPreventer(
  { isPreventingFlicker, hasSetShowLoaderTimeout, children },
  showLoaderTimeoutRef
) {
  useEffect(() => {
    if (!hasSetShowLoaderTimeout) return;

    clearTimeout(showLoaderTimeoutRef.current);
  }, [hasSetShowLoaderTimeout, showLoaderTimeoutRef]);

  if (isPreventingFlicker) {
    return (
      <SDiv>
        <FnrLoadingCarousel show />
      </SDiv>
    );
  }

  return children;
}
